// noinspection CssInvalidPropertyValue,CssInvalidFunction
// import polyfill from '@oddbird/css-anchor-positioning/fn';
import styled, {css} from "styled-components";
import {createGlobalStyle} from "styled-components";
import {createPortal} from "react-dom";
import React, {createContext, useContext, useId, useState} from "react";

// if (!("anchorName" in document.documentElement.style)) {
//   polyfill();
// }

type AnchorTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof BaseTrigger>,
  "$anchor"
>;

export const AnchorTryRules = createGlobalStyle`
    @position-try --flip-vertical {
        top: anchor(top);
        bottom: auto;
    }

    @position-try --flip-horizontal {
        left: anchor(right);
    }
`;

export type Placement =
  | "top left"
  | "top center"
  | "top right"
  | "bottom left"
  | "bottom center"
  | "bottom right"
  | "center left"
  | "center center"
  | "center right";

const AnchorContext = createContext<string | null>(null);
const PortalContainerContext = createContext<Element | null>(null);

export const Anchor = ({children}: { children: React.ReactNode }) => {
  const id = useId();
  const anchorName = `--anchor-${id}`;

  return (
    <AnchorContext.Provider value={anchorName}>
      {children}
    </AnchorContext.Provider>
  );
};

function useAnchorName() {
  const ctx = useContext(AnchorContext);
  if (!ctx) throw new Error("Anchor components must be used inside <Anchor>");
  return ctx;
}


const StyledArrow = styled.div<{ $my: Placement, $at: Placement, $shadow?: boolean, $backgroundColor?: string, $anchor: string, $arrowType?: 'box' | 'triangle', $arrowSize?: number }>`
    position: absolute;
    width: ${({$arrowSize = 16}) => $arrowSize}px;
    height: ${({$arrowSize = 16}) => $arrowSize}px;
    z-index: 1000;
    background: ${props => props.$backgroundColor || "white"};

    ${({$arrowType, $shadow, $arrowSize = 16}) => $arrowType === 'triangle'
        ? css`
            /* no shadow, no rotate — safe for semi-transparent popups */
        `
        : css`
            transform: translate(-50%, -50%) rotate(45deg);
            ${$shadow && css`
                width: ${$arrowSize + 2}px;
                height: ${$arrowSize + 2}px;
                border: 1px solid rgb(229, 229, 229);
                box-shadow: rgba(0, 0, 0, 0.07) 0px 2px 8px;
                z-index: 999;
            `}
        `
    }

    ${({$arrowType, $my, $at, $arrowSize = 16}) => {
        if ($arrowType !== 'triangle') return '';
        const [myV, myH] = $my.split(" ");
        const [atV] = $at.split(" ");
        const dir = myV !== atV
            ? (myV === 'bottom' ? 'down' : 'up')
            : (myH === 'right' ? 'right' : 'left');
        const clipPaths: Record<string, string> = {
            up:    'polygon(50% 0%, 0% 100%, 100% 100%)',
            down:  'polygon(0% 0%, 100% 0%, 50% 100%)',
            left:  'polygon(0% 50%, 100% 0%, 100% 100%)',
            right: 'polygon(0% 0%, 100% 50%, 0% 100%)',
        };
        // transform places the BASE of the triangle at the popup contact edge
        const transforms: Record<string, string> = {
            up:    'translate(-50%, -100%)',
            down:  'translate(-50%, 0%)',
            left:  'translate(-100%, -50%)',
            right: 'translate(0%, -50%)',
        };
        const isVertical = dir === 'up' || dir === 'down';
        return css`
            width: ${isVertical ? $arrowSize : $arrowSize / 2}px;
            height: ${isVertical ? $arrowSize / 2 : $arrowSize}px;
            clip-path: ${clipPaths[dir]};
            transform: ${transforms[dir]};
        `;
    }}

    ${({$my, $at, $anchor, $arrowType}) => {
        const [myV, myH] = $my.split(" ");
        const [atV] = $at.split(" ");
        // For box type, push 1px inward so the rotated borders are hidden inside the popup
        const inset = (side: string) =>
            $arrowType !== 'triangle'
                ? `calc(anchor(${$anchor + '-pop'} ${side}) ${side === 'top' || side === 'left' ? '+' : '-'} 1px)`
                : `anchor(${$anchor + '-pop'} ${side})`;

        if (myV !== atV) {
            return css`
                left: anchor(${$anchor} center);
                top: ${inset(myV)};
            `;
        } else {
            return css`
                top: anchor(${$anchor} center);
                left: ${inset(myH)};
            `;
        }
    }}
`;

const BaseTrigger = styled.div<{ $anchor: string }>`
    anchor-name: ${({$anchor}) => $anchor};
`;

export const AnchorTrigger = React.forwardRef<HTMLDivElement, AnchorTriggerProps>(
  ({...props}, ref) => {
    const anchorName = useAnchorName();

    return (
      <BaseTrigger
        ref={ref} $anchor={anchorName}
        {...props}
      />
    );
  }
);


export const AnchorPortalContainer = ({children}: { children: React.ReactNode }) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  return (
    <PortalContainerContext.Provider value={container}>
      <div ref={setContainer} style={{display: 'contents'}}>
        {children}
      </div>
    </PortalContainerContext.Provider>
  );
};

export const AnchorPortal = ({children}: { children: React.ReactNode }) => {
  const container = useContext(PortalContainerContext);
  if (typeof document === "undefined") return null;
  return createPortal(children, container ?? document.body);
};

const StyledContent = styled.div<{
  $anchor: string;
  $my: Placement;
  $at: Placement;
  $margin: string;
  $arrowGap?: number;
}>`
    position: absolute;
    position-anchor: ${({$anchor}) => $anchor};
    anchor-name: ${({$anchor}) => $anchor + "-pop"};
    position-try: --flip-vertical, --flip-horizontal;

    ${({$my,$at}) => {
        const [myV, myH] = $my.split(" ");
        const [atV, atH] = $at.split(" ");

        if (myV !== atV) {
            // Vertical contact (popup above/below)
            const hCss = myH === 'center'
                ? `justify-self: anchor-center;`
                : `${myH}: anchor(${atH});`;
            return `${myV}: anchor(${atV}); ${hCss}`;
        } else {
            // Horizontal contact (popup left/right)
            const vCss = myV === 'center'
                ? `align-self: anchor-center;`
                : `${myV}: anchor(${atV});`;
            return `${myH}: anchor(${atH}); ${vCss}`;
        }
    }}

    ${({$margin}) =>
            $margin &&
            css`
                margin: ${$margin};
            `}

    ${({$my, $at, $arrowGap}) => {
        if (!$arrowGap) return '';
        const [myV, myH] = $my.split(" ");
        const [atV] = $at.split(" ");
        const side = myV !== atV ? myV : myH;
        return `margin-${side}: ${$arrowGap}px;`;
    }}
    
    z-index: 1000;
`;

export const AnchorContent = React.forwardRef<
  HTMLDivElement,
  {
    my?: Placement;
    at?: Placement;
    margin?: string;
    withArrow?: boolean | React.ElementType;
    arrowColor?: string;
    arrowType?: 'box' | 'triangle';
    arrowSize?: number;
    arrowGap?: number;
  } & React.HTMLAttributes<HTMLDivElement>
>(({my = "top left", at = "top right", margin = "0", arrowColor, withArrow, arrowType = 'box', arrowSize, arrowGap = 0, children, ...props}, ref) => {
  const anchorName = useAnchorName();
  const calculatedArrowGap = withArrow
    ? (arrowType === 'triangle'
      ? (arrowSize ?? 16) / 2
      : Math.round((arrowSize ?? 16) * Math.SQRT2 / 2)) + arrowGap
    : arrowGap || undefined;

  return (<div>
    <StyledContent
      ref={ref} $anchor={anchorName} $my={my} $at={at} $margin={margin} $arrowGap={calculatedArrowGap}
      {...props}
    >
      {children}
    </StyledContent>
    {withArrow && <StyledArrow as={withArrow === true ? undefined : withArrow} $backgroundColor={arrowColor} $my={my} $at={at} $anchor={anchorName} $arrowType={arrowType} $arrowSize={arrowSize}/>}
    {withArrow && arrowType === 'box' && <StyledArrow  $shadow $backgroundColor={arrowColor} $my={my} $at={at} $anchor={anchorName} $arrowType={arrowType} $arrowSize={arrowSize}/>}
  </div>);
});

type PopupProps = {
  children: React.ReactNode;
  at?: Placement;
  my?: Placement;
  margin?: string;
  arrowColor?: string;
  withArrow?: boolean | React.ElementType;
  arrowType?: 'box' | 'triangle';
  arrowSize?: number;
  arrowGap?: number;
};


const AnchorBox = styled.div<{ $anchor: string }>`
  position: absolute;
  inset: 0;

  anchor-name: ${({ $anchor }) => $anchor};

  pointer-events: none;
`;
export function Popup({
                        children,
                        my,
                        at,
                        margin,
                        withArrow,
                        arrowColor,
                        arrowType,
                        arrowSize,
                        arrowGap
                      }: PopupProps) {
  return (
    <Anchor>
      <Anchor.Trigger as={AnchorBox} />

      <Anchor.Portal>
        <AnchorContent
          my={my}
          at={at}
          margin={margin}
          withArrow={withArrow}
          arrowColor={arrowColor}
          arrowType={arrowType}
          arrowSize={arrowSize}
          arrowGap={arrowGap}
        >
          {children}
        </AnchorContent>
      </Anchor.Portal>
    </Anchor>
  );
}

Anchor.Trigger = AnchorTrigger;
Anchor.Content = AnchorContent;
Anchor.Portal = AnchorPortal;
Anchor.PortalContainer = AnchorPortalContainer;
Anchor.Popup = Popup;


